import React from "react";
import { useHistory } from "react-router-dom";

import { Button, Descriptions, message, Space, Tag, Tree } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { clientUrls } from "misc/urls";
import { useCustomers, useRoles } from "misc/api/keycloakApi";
import { buildUrl } from "misc/helpfunctions";
import { Card } from "misc/card";
import { TextField } from "misc/fields";
import { EditableTable } from "misc/tables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SideHeader from "navigation/SideHeader";

const CustomersListComponent = () => {
    const [t] = useTranslation();
    const history = useHistory();

    const { customers, customersQueryState, deleteCustomer, deleteCustomerMutation } = useCustomers({ suspense: true });
    const onDeleteCustomer = customer =>
        deleteCustomer(customer, {
            onSuccess: () => message.success(t("t_customer_delete_success")),
            onError: error => message.error(t(`t_http_error_${error.status}`))
        });

    const columns = [
        {
            title: <TextField value="t_name" />,
            dataIndex: "name",
            key: "name",
            render: (text, record) => record.name,
            sorter: (a, b) => a.name.localeCompare(b.name),
            defaultSortOrder: "ascend"
        },
        {
            title: <TextField value="t_users" />,
            dataIndex: "members",
            key: "members",
            render: (text, record) => record?.members.length,
            width: "100px"
        }
    ];

    const buildRolesTree = roles => {
        const treeData = [];
        const clientMap = new Map();
        const clientRoles = roles.filter(role => role.client);
        clientRoles.forEach(role => {
            const { name, client, id, permissions } = role;

            if (!clientMap.has(client)) {
                clientMap.set(client, { title: client.charAt(0).toUpperCase() + client.slice(1), key: client, children: [] });
                treeData.push(clientMap.get(client));
            }

            const roleNode = {
                title: name.startsWith("role_") ? name.replace("role_", "") : name,
                key: `${client}-${name}`
            };

            if (permissions && permissions.length > 0) {
                roleNode.children = permissions.map(permission => ({
                    title: permission.description || permission.name,
                    key: `${client}-${name}-${permission.id}`
                }));
            }

            clientMap.get(client).children.push(roleNode);
        });

        return treeData;
    };

    const expandedRowRender = rowData => {
        const owners = rowData.members.filter(member => member?.organizationRoles?.includes("org-root-user"));
        const userColumns = [
            {
                title: <TextField value="t_name" />,
                key: "name",
                render: (text, record) => {
                    const fullName = `${record.firstName ?? ""} ${record.lastName ?? ""}`.trim();
                    return fullName || "";
                },
                width: "50%",
                sorter: (a, b) => {
                    const fullNameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
                    const fullNameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim();
                    return fullNameA.localeCompare(fullNameB);
                },
                defaultSortOrder: "ascend"
            },
            {
                title: <TextField value="t_user_email" />,
                dataIndex: "email",
                key: "email",
                render: (text, record) => record.email,
                width: "50%"
            }
        ];

        return (
            <Card>
                <Descriptions title={<TextField value="t_customer_details" />} column={1} bordered labelStyle={{ width: 300 }}>
                    <Descriptions.Item label={<TextField value="t_customer_owner" />}>
                        {owners.length > 0
                            ? owners.map(owner => (
                                  <Tag key={owner.email}>
                                      <TextField value={owner.email} />
                                  </Tag>
                              ))
                            : ""}
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_apps_permissions" />}>
                        <Tree showLine switcherIcon={<DownOutlined />} treeData={buildRolesTree(rowData?.roles)} style={{ marginTop: "-10px" }} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<TextField value="t_users" />}>
                        <EditableTable
                            scroll={{ x: "100%" }} // fixed table not span the full width
                            style={{ marginLeft: "-40px" }}
                            enumerate={false}
                            dataSource={rowData.members}
                            columns={userColumns}
                            rowKey="username"
                        />
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        );
    };

    return (
        <Card title="t_customers" height={700} stretchHeight>
            <EditableTable
                dataSource={customers.map(customer => ({ ...customer, editable: true, deletable: true }))}
                columns={columns}
                rowKey="id"
                expandable={{ expandedRowRender }}
                onDeleteItem={onDeleteCustomer}
                extraActions={record => [
                    {
                        disabled: !record.editable,
                        tooltip: "t_customer_edit",
                        key: `edit ${record.id}`,
                        icon: <FontAwesomeIcon icon={["fal", "pen-to-square"]} />,
                        onClick: () =>
                            history.push(
                                buildUrl({
                                    path: clientUrls.customers.edit(),
                                    queryParameters: { customerId: record.id }
                                })
                            )
                    }
                ]}
                extraHeader={
                    <Button onClick={() => history.push(clientUrls.customers.create())}>
                        <Space>
                            <FontAwesomeIcon icon={["fal", "plus"]} />
                            <TextField value="t_customer_create" />
                        </Space>
                    </Button>
                }
                isLoading={customersQueryState.isLoading || customersQueryState.isRefetching || deleteCustomerMutation.isLoading}
            />
        </Card>
    );
};

export default CustomersListComponent;
